import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Users, DollarSign, Calendar, Eye, CheckCircle, XCircle, Clock, Filter, Award, TrendingDown, Receipt, BarChart3, AlertTriangle, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AffiliatePendingPayments from './AffiliatePendingPayments';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperadminAffiliateDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [missingData, setMissingData] = useState({ affiliates: [], total: 0, with_issues: 0 });
  const [showMissingData, setShowMissingData] = useState(false);
  const [kpis, setKpis] = useState({
    total_affiliates: 0,
    active_affiliates: 0,
    pending_affiliates: 0,
    total_customers: 0,
    total_revenue: 0,
    total_commissions: 0,
    pending_payouts: 0
  });
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [leaderboard, setLeaderboard] = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    loadData();
  }, [timeRange]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      // Load statistics with time filter
      const statsResponse = await axios.get(
        `${API_URL}/api/superadmin/affiliates/statistics?time_range=${timeRange}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setKpis(statsResponse.data.kpis);
      
      // Load all affiliates
      const affiliatesResponse = await axios.get(`${API_URL}/api/superadmin/affiliates/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setAffiliates(affiliatesResponse.data);
      
      // Load missing data overview
      const missingResp = await axios.get(`${API_URL}/api/superadmin/affiliates/missing-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => ({ data: { affiliates: [], total: 0, with_issues: 0 } }));
      setMissingData(missingResp.data);
      
      // Generate Leaderboard (Top 10 Affiliates by total earnings)
      const sortedAffiliates = [...affiliatesResponse.data]
        .sort((a, b) => (b.total_earnings || 0) - (a.total_earnings || 0))
        .slice(0, 10);
      setLeaderboard(sortedAffiliates);
      
      // Generate Chart Data (monthly commissions aggregate)
      const commissions = await axios.get(`${API_URL}/api/superadmin/affiliates/commissions/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      const monthlyData = {};
      (commissions.data || []).forEach(comm => {
        const period = comm.period || 'Unknown';
        if (!monthlyData[period]) {
          monthlyData[period] = { period, revenue: 0, commission: 0 };
        }
        monthlyData[period].revenue += comm.revenue_total_net || 0;
        monthlyData[period].commission += comm.commission_amount || 0;
      });
      
      const chartArray = Object.values(monthlyData).sort((a, b) => a.period.localeCompare(b.period));
      setChartData(chartArray.slice(-6)); // Last 6 months
      
      setError('');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (affiliateId) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(
        `${API_URL}/api/superadmin/affiliates/${affiliateId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      loadData(); // Reload
    } catch (err) {
      alert('Fehler beim Genehmigen');
    }
  };

  const handleReject = async (affiliateId) => {
    const reason = prompt('Grund für Ablehnung:');
    if (!reason) return;
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(
        `${API_URL}/api/superadmin/affiliates/${affiliateId}/reject`,
        { reason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      loadData(); // Reload
    } catch (err) {
      alert('Fehler beim Ablehnen');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/superadmin')}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
                title="Zurück zum Superadmin-Dashboard"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  Affiliate Dashboard
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  Übersicht aller Affiliates, Kunden und Provisionen
                </p>
              </div>
            </div>
          </div>
          
          {/* Actions & Filters - Responsive */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => navigate('/superadmin/analytics')}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base min-h-[44px]"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Analytics</span>
                <span className="sm:hidden">Analytics</span>
              </button>
              
              <button
                onClick={() => navigate('/superadmin/gutschriften')}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base min-h-[44px]"
              >
                <Receipt className="w-4 h-4" />
                <span>Gutschriften</span>
              </button>

              {missingData.with_issues > 0 && (
                <button
                  data-testid="missing-data-toggle"
                  onClick={() => setShowMissingData(!showMissingData)}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base min-h-[44px]"
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span>Fehlende Daten ({missingData.with_issues})</span>
                  {showMissingData ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              )}
            </div>
            
            {/* Time Range Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm sm:text-base min-h-[44px]"
              >
                <option value="today">Heute</option>
                <option value="week">7 Tage</option>
                <option value="month">Monat</option>
                <option value="quarter">Quartal</option>
                <option value="year">Jahr</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Offene Zahlungen / Prüffälle / Nachzahlungen */}
        <AffiliatePendingPayments />

        {/* Missing Data Section */}
        {showMissingData && missingData.with_issues > 0 && (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden" data-testid="missing-data-section">
            <div className="flex items-center justify-between p-4 border-b border-amber-200 bg-amber-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <h2 className="text-base font-bold text-amber-800">
                  Partner mit fehlenden Daten ({missingData.with_issues} von {missingData.total})
                </h2>
              </div>
              <button onClick={() => setShowMissingData(false)} className="text-amber-600 hover:text-amber-800 text-sm font-medium">
                Ausblenden
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="px-4 py-3 text-left font-semibold text-amber-800">Partner</th>
                    <th className="px-4 py-3 text-left font-semibold text-amber-800">E-Mail</th>
                    <th className="px-4 py-3 text-left font-semibold text-amber-800">USt-Modus</th>
                    <th className="px-4 py-3 text-left font-semibold text-amber-800">Fehlende Informationen</th>
                  </tr>
                </thead>
                <tbody>
                  {missingData.affiliates.filter(a => a.has_issues).map((aff) => (
                    <tr key={aff.id} className="border-t border-amber-200 hover:bg-amber-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{aff.name}</td>
                      <td className="px-4 py-3 text-gray-600">{aff.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          aff.vat_mode === 'small_business'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {aff.vat_mode === 'small_business' ? 'Kleinunternehmer' : 'Regelbesteuert'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {aff.missing_fields.map((field, i) => (
                          <span key={i} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                            {field}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Affiliates Gesamt
              </h3>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {kpis.total_affiliates}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {kpis.active_affiliates} aktiv, {kpis.pending_affiliates} ausstehend
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Geworbene Kunden
              </h3>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {kpis.total_customers}
            </p>
            <div className="flex gap-3 mt-2">
              <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium">
                {kpis.trial_customers ?? 0} Testphase
              </span>
              <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full font-medium">
                {kpis.active_paying_customers ?? 0} Aktiv (bezahlt)
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Umsatz über Affiliates
              </h3>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(kpis.total_revenue)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Im gewählten Zeitraum
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Provisionen Gesamt
              </h3>
              <DollarSign className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(kpis.total_commissions)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Ausgezahlt: {formatCurrency(kpis.paid_commissions)}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Offene Auszahlungen
              </h3>
              <Calendar className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(kpis.pending_commissions)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Wartet auf Bearbeitung
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue & Commission Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
              Umsatz & Provision (Verlauf)
            </h2>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => `${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Umsatz" />
                  <Line type="monotone" dataKey="commission" stroke="#10b981" strokeWidth={2} name="Provision" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                Keine Daten vorhanden
              </p>
            )}
          </div>

          {/* Leaderboard */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-yellow-500" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Top 10 Affiliates
              </h2>
            </div>
            {leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.map((affiliate, index) => (
                  <div 
                    key={affiliate.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-50 text-blue-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {affiliate.first_name} {affiliate.last_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {affiliate.active_customers || 0} Kunden
                          {(affiliate.trial_customers ?? 0) > 0 && (
                            <span className="ml-1 text-amber-600">({affiliate.trial_customers} Test)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(affiliate.total_earnings)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                Keine Daten vorhanden
              </p>
            )}
          </div>
        </div>

        {/* Affiliates Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
              Alle Affiliates
            </h2>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900 px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Affiliate
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kunden
                    </th>
                    <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Provisionen
                    </th>
                    <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Registriert
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {affiliates.map((affiliate) => (
                  <tr key={affiliate.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-3 sm:px-6 py-4">
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {affiliate.first_name} {affiliate.last_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px] sm:max-w-none">
                          {affiliate.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
                        {affiliate.affiliate_code}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        affiliate.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : affiliate.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        <CheckCircle className={`w-3 h-3 mr-1 ${affiliate.status !== 'active' && 'hidden'}`} />
                        <Clock className={`w-3 h-3 mr-1 ${affiliate.status !== 'pending' && 'hidden'}`} />
                        <XCircle className={`w-3 h-3 mr-1 ${affiliate.status !== 'rejected' && 'hidden'}`} />
                        <span className="hidden sm:inline">{affiliate.status}</span>
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">
                          {(affiliate.trial_customers ?? 0) + (affiliate.active_customers_count ?? affiliate.active_customers ?? 0)}
                        </span>
                        <div className="flex gap-1">
                          {(affiliate.trial_customers ?? 0) > 0 && (
                            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full">
                              {affiliate.trial_customers} Test
                            </span>
                          )}
                          {(affiliate.active_customers_count ?? 0) > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-1.5 py-0.5 rounded-full">
                              {affiliate.active_customers_count} Aktiv
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(affiliate.total_earnings)}
                    </td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(affiliate.created_at)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-1 sm:gap-2">
                        <button
                          onClick={() => navigate(`/superadmin/affiliates/${affiliate.id}`)}
                          className="p-2 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                          title="Details anzeigen"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {affiliate.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(affiliate.id)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReject(affiliate.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {affiliates.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Keine Affiliates vorhanden
            </div>
          )}
        </div>

        {/* Affiliate Details Modal */}
        {selectedAffiliate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Affiliate Details
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {selectedAffiliate.first_name} {selectedAffiliate.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {selectedAffiliate.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Affiliate Code</p>
                    <p className="font-mono font-medium text-gray-900 dark:text-gray-100">
                      {selectedAffiliate.affiliate_code}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {selectedAffiliate.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">USt-Status</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {selectedAffiliate.vat_mode === 'small_business' ? 'Kleinunternehmer' : 'Regelbesteuert'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">IBAN</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {selectedAffiliate.iban_formatted || selectedAffiliate.iban || '-'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setSelectedAffiliate(null)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperadminAffiliateDashboard;
